import { type Day } from '@choco-admin/types';
import { useCallback } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import { useUpdateDeliverySettingMutation } from '../../../generated';

interface UseUpdateDeliverySettingConfig {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface DaySettings {
  type: 'strict' | 'flexible' | 'noCutoff';
  leadTime?: number;
  cutoffTime?: string;
}

export const useUpdateDeliverySetting = ({
  onSuccess,
  onError,
}: UseUpdateDeliverySettingConfig = {}) => {
  const { addNotification } = useNotification();
  const [updateDeliverySetting, { loading }] =
    useUpdateDeliverySettingMutation();

  const updateSettings = useCallback(
    async ({
      chatId,
      deliveryDays,
      cutoffType,
      cutoffTimes,
      cutoffDays,
    }: {
      chatId: string;
      deliveryDays: Record<Day, boolean>;
      cutoffType: string;
      cutoffTimes: Record<Day, string>;
      cutoffDays: Record<Day, string>;
    }) => {
      try {
        // Only include days that are enabled
        const enabledDays = Object.entries(deliveryDays).reduce<
          Record<string, DaySettings>
        >((acc, [day, isEnabled]) => {
          if (isEnabled) {
            const shortDay = day.toLowerCase().slice(0, 3);
            if (cutoffType === 'no_cutoff') {
              acc[shortDay] = {
                type: 'noCutoff',
              };
            } else {
              acc[shortDay] = {
                type: cutoffType === 'strict' ? 'strict' : 'flexible',
                ...(cutoffType === 'strict' && {
                  cutoffTime: cutoffTimes[day as Day],
                }),
                leadTime: parseInt(cutoffDays[day as Day], 10),
              };
            }
          }
          return acc;
        }, {});

        await updateDeliverySetting({
          variables: {
            id: chatId,
            cutoffTimes: enabledDays,
          },
        });

        addNotification({
          title: 'Success',
          message: 'Delivery settings updated successfully',
          type: 'success',
        });

        onSuccess?.();
      } catch (error) {
        addNotification({
          title: 'Error',
          message: 'Failed to update delivery settings',
          type: 'error',
        });
        onError?.(error as Error);
      }
    },
    [updateDeliverySetting, addNotification, onSuccess, onError]
  );

  return {
    updateSettings,
    isUpdating: loading,
  };
};
